import React, { useEffect, useState } from "react";

import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import { CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import { WorkspaceFolderCloseIcon } from "assets/material-custom-icons";
import { DealBase, IFolder } from "store/types";

const ROW_HEIGHT = 36;
const CHEVRON_SIZE = 20;
const chevronColor = "#0A3142";
const iconColor = "#0A3142";
const labelColor = "#20293C";

interface Props {
  deal: DealBase;
  isOpen: boolean;
  isSelected: boolean;
  hasFoldersLoaded: boolean;
  getOrLoadDealFolders: (dealId: number) => Promise<IFolder[]>;
  onToggle: (dealId: number) => void;
  onDealClick?: (dealId: number) => void;
}

export const DealFolder: React.FC<Props> = React.memo(
  ({ deal, isOpen, isSelected, hasFoldersLoaded, getOrLoadDealFolders, onToggle, onDealClick }) => {
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
      if (!isOpen || hasFoldersLoaded || isLoading) return;
      setIsLoading(true);
      getOrLoadDealFolders(deal.id).finally(() => setIsLoading(false));
    }, [isOpen, hasFoldersLoaded, isLoading, getOrLoadDealFolders, deal.id]);

    const showSpinner = isOpen && isLoading;

    return (
      <Stack
        direction="row"
        alignItems="center"
        gap="5px"
        sx={{
          width: "100%",
          height: ROW_HEIGHT,
          padding: "0 5px",
          borderRadius: "20px",
          paddingLeft: "5px",
          backgroundColor: isSelected ? "#DBE8F5" : "transparent",
          "&:hover": {
            backgroundColor: "#ECF1F8"
          }
        }}
      >
        <IconButton onClick={() => onToggle(deal.id)} sx={{ cursor: "pointer" }}>
          {showSpinner ? (
            <CircularProgress size={CHEVRON_SIZE} sx={{ color: chevronColor }} />
          ) : (
            <ExpandMoreOutlinedIcon
              sx={{
                fill: chevronColor,
                fontSize: CHEVRON_SIZE,
                transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform 0.2s ease"
              }}
            />
          )}
        </IconButton>
        <Stack
          direction="row"
          alignItems="center"
          gap="5px"
          onClick={(e) => {
            e.stopPropagation();
            onDealClick?.(deal.id);
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            cursor: "pointer"
          }}
        >
          <WorkspaceFolderCloseIcon sx={{ fill: iconColor, fontSize: 20 }} />
          <Typography
            noWrap
            sx={{
              color: labelColor,
              fontWeight: 400,
              fontSize: "16px"
            }}
          >
            {deal.title}
          </Typography>
        </Stack>
      </Stack>
    );
  }
);
